import { LoadingArea } from '@consigli/facade';
import {
  useProjectId,
  useLayoutId,
  useLayoutResults,
  useGetLayoutOptimizationQuery,
} from '@consigli/hooks';
import { convertFileToDocumentWithFileType } from '@consigli/types';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { CardBasedFileDisplay, RESULT_SECTIONS } from '@/components/card-based-file-display';
import { DismissibleDisclaimer } from '@/components/dismissible-disclaimer';
import { WaitingForAnalysis } from '@/components/waiting-for-analysis';
import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';

export const EngineeringResultPage: FC = () => {
  const projectId = useProjectId();
  const layoutId = useLayoutId();
  const { t } = useTranslation();

  const { results, isLoading, isFetching } = useLayoutResults({
    projectId,
    layoutId,
    page: 'all',
  });

  const { data: layout } = useGetLayoutOptimizationQuery({ projectId, layoutId });

  const isPublished = layout ? layout.published : false;

  return (
    <>
      <Breadcrumb label={t('optimization-tabs.results')} />
      <WorkspaceProvider>
        <WorkspaceLayout>
          {isLoading ? (
            <LoadingArea title={t('document-list.loading')} />
          ) : !isPublished || results.length === 0 ? (
            <WaitingForAnalysis />
          ) : (
            <>
              <DismissibleDisclaimer>{t('optimization-tabs.ai-disclaimer')}</DismissibleDisclaimer>
              <div className="flex">
                <CardBasedFileDisplay
                  files={results.map((result) =>
                    convertFileToDocumentWithFileType(result.file, result.type),
                  )}
                  isFetching={isFetching}
                  isPublished={isPublished}
                  sections={RESULT_SECTIONS}
                />
              </div>
            </>
          )}
        </WorkspaceLayout>
      </WorkspaceProvider>
    </>
  );
};
